import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { HOME } from "@/lib/routes";
import { CoachOverlay, findVisible, type GuideStep } from "./CoachOverlay";
import { WelcomeDialog } from "./WelcomeDialog";

type SessionModule = typeof import("./session");

type TrainingContext = {
  /** Practice data is on screen and nothing reaches the backend. */
  active: boolean;
  /** Open training at a chapter (the first by default). */
  start: (chapterId?: string) => Promise<void>;
  exit: () => void;
  /** Chapters this person has finished on this browser, for ticks on the training page. */
  done: string[];
  markDone: (chapterId: string) => void;
  /** Walk the parts of the current screen, one spotlight at a time. */
  showAround: (title: string, steps: GuideStep[]) => void;
  guiding: boolean;
};

const Ctx = createContext<TrainingContext | null>(null);

/** Null outside the provider (unit tests render screens without it); callers hide their entry. */
export const useTraining = () => useContext(Ctx);

const Session = lazy(() => import("./session").then((m) => ({ default: m.TrainingSession })));

const read = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private window: the welcome may show again, which is harmless */
  }
};

export const seenKey = (userId: string) => `vs-training-seen:${userId}`;
const doneKey = (userId: string) => `vs-training-done:${userId}`;
/** Set to "off" to never offer the welcome on this browser (the end-to-end suite does). */
export const WELCOME_SWITCH = "vs-training-welcome";

const NO_WELCOME = /^\/(login|accept|feedback|training)(\/|$)/;

export function TrainingProvider({ children }: { children: React.ReactNode }) {
  const { status, user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [chapter, setChapter] = useState<string | null>(null);
  // Bumped on every start, so choosing the chapter you are already in starts it over.
  const [run, setRun] = useState(0);
  const session = useRef<SessionModule | null>(null);
  const [welcomeClosed, setWelcomeClosed] = useState(false);
  const [doneVersion, setDoneVersion] = useState(0);
  const [guide, setGuide] = useState<{ title: string; steps: GuideStep[]; at: number } | null>(
    null,
  );

  const userId = user?.id ?? null;
  const done = useMemo<string[]>(() => {
    if (!userId) return [];
    try {
      return JSON.parse(read(doneKey(userId)) ?? "[]") as string[];
    } catch {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- doneVersion re-reads storage
  }, [userId, doneVersion]);

  const markDone = useCallback(
    (id: string) => {
      if (!userId || done.includes(id)) return;
      write(doneKey(userId), JSON.stringify([...done, id]));
      setDoneVersion((v) => v + 1);
    },
    [userId, done],
  );

  const start = useCallback(
    async (chapterId?: string) => {
      if (userId) write(seenKey(userId), "1");
      setWelcomeClosed(true);
      setGuide(null);
      const mod = (session.current ??= await import("./session"));
      mod.begin();
      setChapter(chapterId ?? mod.FIRST_CHAPTER);
      setRun((r) => r + 1);
    },
    [userId],
  );

  const exit = useCallback(() => {
    // Unmount every screen that could still poll before the backend becomes reachable again.
    flushSync(() => setChapter(null));
    session.current?.end();
    navigate(HOME);
  }, [navigate]);

  const showAround = useCallback((title: string, steps: GuideStep[]) => {
    // Parts that are not on screen (an empty list, a control your role cannot use) are skipped.
    const here = steps.filter((s) => !s.target || findVisible(s.target));
    if (here.length) setGuide({ title, steps: here, at: 0 });
  }, []);

  const value = useMemo<TrainingContext>(
    () => ({
      active: chapter !== null,
      start,
      exit,
      done,
      markDone,
      showAround,
      guiding: guide !== null,
    }),
    [chapter, start, exit, done, markDone, showAround, guide],
  );

  const offerWelcome =
    status === "authenticated" &&
    !!userId &&
    chapter === null &&
    !welcomeClosed &&
    !NO_WELCOME.test(pathname) &&
    read(WELCOME_SWITCH) !== "off" &&
    !read(seenKey(userId));

  const step = guide?.steps[guide.at];
  return (
    <Ctx.Provider value={value}>
      {chapter !== null ? (
        <Suspense fallback={null}>
          <Session chapter={chapter} run={run} onChapter={setChapter} onExit={exit}>
            {children}
          </Session>
        </Suspense>
      ) : (
        children
      )}
      {offerWelcome && (
        <WelcomeDialog
          onStart={() => void start()}
          onLater={() => {
            write(seenKey(userId), "1");
            setWelcomeClosed(true);
          }}
        />
      )}
      {guide && step && (
        <CoachOverlay
          step={step}
          progress={`${guide.title} · ${guide.at + 1} of ${guide.steps.length}`}
          onBack={guide.at > 0 ? () => setGuide({ ...guide, at: guide.at - 1 }) : undefined}
          primary={{
            label: guide.at + 1 < guide.steps.length ? "Next" : "Done",
            onClick: () =>
              setGuide(guide.at + 1 < guide.steps.length ? { ...guide, at: guide.at + 1 } : null),
          }}
          onExit={() => setGuide(null)}
          exitLabel="Stop showing me around"
        />
      )}
    </Ctx.Provider>
  );
}
